import React, { ReactNode, useMemo } from 'react';
import {
  FormData,
  NavItemType,
  PostResponse,
  Props,
  Mode,
  Type
} from './addJobPost.type';
import PageBreadcrumb, {
  PageBreadcrumbItem
} from 'components/common/PageBreadcrumb';
import Button from 'components/base/Button';
import { Link, useNavigate } from 'react-router-dom';
import JobPostBasicForm from './form/basic/JobPostBasicForm';
import { Col, Nav, Row, Tab } from 'react-bootstrap';
import { navItems, TabValues, validateTab } from './config';
import classNames from 'classnames';
import FeatherIcon from 'feather-icons-react';
import { Form, Formik } from 'formik';
import JobPostSkillsForm from './form/skill/JobPostSkillForm';
import JobPostApplicationForm from './form/application/JobPostApplicationForm';
import JobPostAdditionalForm from './form/others/JobPostAdditionalForm';
import JobPostContactForm from './form/contact/JobPostContactForm';
import * as Yup from 'yup';
import {
  useCreateInternshipMutation,
  useCreateJobPostMutation,
  useFindOneJobPostQuery,
  useUpdateInternshipMutation,
  useUpdateJobPostMutation
} from 'service/recruiterQuery/recruiterQuery';
import { transformInputDate } from 'helpers/utils';

const goBackPath: Record<Type, string> = {
  internship: '/panel/recruitment/careers/internships',
  post: '/panel/recruitment/careers/jobs'
};

const titleMap: Record<Type, Record<Mode, string>> = {
  internship: {
    add: 'Add a internship',
    edit: 'Edit a internship'
  },
  post: {
    add: 'Add a job post',
    edit: 'Edit a job post'
  }
};

const descMap: Record<Type, Record<Mode, string>> = {
  internship: {
    add: 'Enter details to add a new internship.',
    edit: 'Change details to update a internship.'
  },
  post: {
    add: 'Enter details to add a new job post.',
    edit: 'Change details to update a job post.'
  }
};

const NavItem = ({
  item,
  isError
}: {
  item: NavItemType;
  isError?: boolean;
}) => {
  return (
    <Nav.Link
      eventKey={item.eventKey}
      className={classNames(
        'text-center text-sm-start cursor-pointer outline-none d-sm-flex align-items-sm-center border-bottom-sm border-end border-end-sm-0'
      )}
    >
      <FeatherIcon
        icon={item.icon}
        size={16}
        className={classNames('me-sm-2 nav-icons', {
          'text-danger': isError
        })}
      />
      <span
        className={classNames('d-none d-sm-inline', {
          'text-danger': isError
        })}
      >
        {item.label}
      </span>
    </Nav.Link>
  );
};

function AddJobPost({ mode, id, type }: Props) {
  const [createJobPost, { isLoading }] = useCreateJobPostMutation();
  const [createInternship, { isLoading: internshipLoader }] =
    useCreateInternshipMutation();
  const [updateInternship, { isLoading: updateInternshipLoader }] =
    useUpdateInternshipMutation();
  const [updateJobPost, { isLoading: updateJobPostLoader }] =
    useUpdateJobPostMutation();
  const { data } = useFindOneJobPostQuery(id, {
    skip: mode === 'add' && !!id
  });

  const navigate = useNavigate();

  const isInternship = type === 'internship';

  const validationSchema = Yup.object({
    deadline: Yup.date()
      .required('Deadline is required')
      .min(new Date(), 'Deadline must be in the future'),
    linkOrEmail: Yup.string()
      .required('Link or Email is required')
      .test(
        'is-valid',
        'Must be a valid email or URL',
        value =>
          Yup.string().email().isValidSync(value) ||
          Yup.string().url('Must be a valid URL').isValidSync(value)
      ),
    numberOfOpenings: Yup.number()
      .required('Number of openings is required')
      .integer('Number of openings must be an integer')
      .positive('Number of openings must be a positive number'),
    title: Yup.string()
      .required('Title is required')
      .min(3, 'Title must be at least 3 characters')
      .max(100, 'Title must be less than 100 characters'),
    desc: Yup.string()
      .required('Description is required')
      .min(10, 'Description must be at least 10 characters'),
    companyName: Yup.string()
      .required('Company Name is required')
      .min(2, 'Company Name must be at least 2 characters'),
    jobLocation: Yup.string()
      .required('Job Location is required')
      .min(3, 'Job Location must be at least 3 characters')
      .max(100, 'Job Location must be less than 100 characters'),
    contactPersonEmail: Yup.string()
      .required('Email is required')
      .email('Invalid email address'),
    contactPersonName: Yup.string()
      .required('Name is required')
      .min(2, 'Name must be at least 2 characters')
      .max(50, 'Name must be less than 50 characters'),
    role: Yup.string().required('Role is required'),
    skills: Yup.array()
      .of(Yup.string().required('Each skill must be a valid string'))
      .min(1, 'At least one skill is required'),
    qualification: Yup.string().required('Qualification is required'),
    ...(isInternship
      ? {
          modeOfWork: Yup.string().required('Mode of work is required'),
          internshipDuration: Yup.number()
            .required('Internship duration is required')
            .positive('Internship duration must be a positive number')
            .integer('Internship duration must be an integer')
        }
      : {
          employmentType: Yup.string().required('Employment Type is required'),
          experience: Yup.string().required('Experience is required')
        })
  });

  const breadcrumbItemsMap: Record<
    Type,
    Record<Mode, PageBreadcrumbItem[]>
  > = useMemo(() => {
    return {
      internship: {
        add: [
          {
            label: 'Internship',
            url: '/panel/recruitment/careers/internships'
          },
          {
            label: 'Add internship',
            url: '/panel/recruitment/careers/internships/add',
            active: true
          }
        ],
        edit: [
          {
            label: 'Internship',
            url: '/panel/recruitment/careers/internships'
          },
          {
            label: 'Edit internship',
            url: `/panel/recruitment/careers/internships/edit/${id}`,
            active: true
          }
        ]
      },
      post: {
        add: [
          {
            label: 'Job Post',
            url: '/panel/recruitment/careers/jobs'
          },
          {
            label: 'Add job post',
            url: '/panel/recruitment/careers/jobs/add',
            active: true
          }
        ],
        edit: [
          {
            label: 'Job Post',
            url: '/panel/recruitment/careers/jobs'
          },
          {
            label: 'Edit job post',
            url: `/panel/recruitment/careers/jobs/edit/${id}`,
            active: true
          }
        ]
      }
    };
  }, []);

  const jobPost = data as PostResponse;

  const actionButtonMap: Record<Type, Record<Mode, ReactNode>> = {
    internship: {
      add: (
        <Button
          variant="primary"
          type="submit"
          loading={internshipLoader}
          loadingPosition="start"
        >
          Publish Internship
        </Button>
      ),
      edit: (
        <Button
          variant="primary"
          type="submit"
          loading={updateInternshipLoader}
          loadingPosition="start"
        >
          Update
        </Button>
      )
    },
    post: {
      add: (
        <Button
          variant="primary"
          type="submit"
          loading={isLoading}
          loadingPosition="start"
        >
          Publish Job Post
        </Button>
      ),
      edit: (
        <Button
          variant="primary"
          type="submit"
          loading={updateJobPostLoader}
          loadingPosition="start"
        >
          Update
        </Button>
      )
    }
  };

  const handleSubmit = (
    values: FormData,
    { resetForm }: { resetForm: () => void }
  ) => {
    if (mode === 'add') {
      const handler = type === 'post' ? createJobPost : createInternship;
      handler(values)
        .unwrap()
        .then(() => {
          resetForm();
        })
        .catch(console.log);
    }
    if (mode === 'edit') {
      const updateHandler = type === 'post' ? updateJobPost : updateInternship;
      updateHandler({ id, ...values })
        .unwrap()
        .then(() => {
          resetForm();
          navigate(goBackPath[type]);
        })
        .catch(console.log);
    }
  };

  return (
    <div>
      <PageBreadcrumb items={breadcrumbItemsMap[type][mode]} />
      <Formik
        initialValues={{
          deadline: transformInputDate(jobPost?.deadline) || '',
          linkOrEmail: jobPost?.linkOrEmail || '',
          numberOfOpenings: jobPost?.numberOfOpenings?.toString() || '',
          title: jobPost?.title || '',
          desc: jobPost?.desc || '',
          companyName: jobPost?.companyName || '',
          jobLocation: jobPost?.jobLocation || '',
          employmentType: jobPost?.employmentType || '',
          salaryRange: jobPost?.salaryRange || [5000, 150000],
          experience: jobPost?.experience || 'flexible',
          contactPersonEmail: jobPost?.contactPersonEmail || '',
          contactPersonName: jobPost?.contactPersonName || '',
          benefits: jobPost?.benefits || '',
          workingHours: jobPost?.workingHours || '',
          role: jobPost?.role?._id || '',
          skills: Array.isArray(jobPost?.skills)
            ? jobPost.skills.map(({ _id }) => _id)
            : [],
          qualification: jobPost?.qualification || '',
          isBillable: jobPost?.isBillable || false,
          modeOfWork: jobPost?.modeOfWork || '',
          internshipDuration: jobPost?.internshipDuration?.toString() || ''
        }}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched }) => {
          const role = values['role'];
          const isBillable = values['isBillable'];
          const errorFields = Object.keys(errors);
          const touchedFields = Object.keys(touched);
          const tabStatus: Record<TabValues, boolean> = validateTab(
            errorFields,
            touchedFields
          );
          return (
            <Form>
              <section className="mb-9">
                <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
                  <div>
                    <h2 className="mb-2">{titleMap[type][mode]}</h2>
                    <h5 className="text-body-tertiary fw-semibold">
                      {descMap[type][mode]}
                    </h5>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      variant="phoenix-secondary"
                      as={Link}
                      to={goBackPath[type]}
                    >
                      Back
                    </Button>
                    {actionButtonMap[type][mode]}
                  </div>
                </div>
                <div className="mb-5">
                  <JobPostBasicForm
                    isInternship={isInternship}
                    isBillable={isBillable}
                  />
                </div>
                <div>
                  <h4 className="mb-3">Additional Information</h4>
                  <Tab.Container
                    id="left-tabs-example"
                    defaultActiveKey="skill"
                  >
                    <Row className="g-0 border-top border-bottom">
                      <Col xs={12} sm={4}>
                        <Nav className="flex-sm-column border-bottom border-bottom-sm-0 border-end-sm fs-9 vertical-tab h-100 justify-content-between">
                          {navItems.map(item => (
                            <NavItem
                              key={item.label}
                              item={item}
                              isError={tabStatus[item.eventKey]}
                            />
                          ))}
                        </Nav>
                      </Col>
                      <Col xs={12} sm={8}>
                        <Tab.Content className="py-3 ps-sm-4 h-100">
                          <Tab.Pane eventKey="skill" className="h-100">
                            <h5 className="mb-3 text-body-highlight">
                              Role and Skills Information
                            </h5>
                            <JobPostSkillsForm role={role} />
                          </Tab.Pane>
                          <Tab.Pane eventKey="application" className="h-100">
                            <h5 className="mb-3 text-body-highlight">
                              Application Information
                            </h5>
                            <JobPostApplicationForm />
                          </Tab.Pane>
                          <Tab.Pane eventKey="contact" className="h-100">
                            <h5 className="mb-3 text-body-highlight">
                              Contact Information
                            </h5>
                            <JobPostContactForm />
                          </Tab.Pane>
                          <Tab.Pane eventKey="others" className="h-100">
                            <h5 className="mb-3 text-body-highlight">
                              Supplementary Information
                            </h5>
                            <JobPostAdditionalForm />
                          </Tab.Pane>
                        </Tab.Content>
                      </Col>
                    </Row>
                  </Tab.Container>
                </div>
              </section>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}

export default AddJobPost;
